/**
 * @module models/api/payment
 */

import _ from 'lodash'

import { log } from '@/log'
import Resource from '@/models/api/resource'
import {
  CreditCardKind,
  CreditCardType,
  PaymentKind,
  PaymentType,
} from '@/models/app-config/behavior/types'
import ParameterFactory from './parameter/factory'
import Parameter, { ParameterList } from './parameter/parameter'
import UserSelect from './parameter/type/user-select'

type CreditCardPayment = Payment
  & {
    readonly id: CreditCardType
    readonly kind: CreditCardKind
  }

export class Payment extends Resource {

  get id(): PaymentType {
    return this.get('id')
  }

  get kind(): PaymentKind {
    return this.get('kind')
  }
  isCreditCard(): this is CreditCardPayment {
    /**
     * 本来はこの実装通り「分類（kind）」による判断としたいが
     * {@see module:store/modules/orderConfirm/types.isCreditCard} では
     * idによる分岐をやむなく残している. 詳細は参照先のコメントを参照.
     */
    return this.kind === 'creditcard'
  }

  get label(): string {
    return this.get('label', '')
  }

  get supplement(): string[] {
    return this.get('supplement', [])
  }

  get description(): string {
    return this.get('description', '')
  }

  /**
   * 1回の購入手続きで複数注文を行えるか
   *
   * 実はこの知識はサーバーから取得するのでなく、クライアント側で持つべきかもしれないという議論あり
   * @see https://github.com/Everforth/growthpalette-front/pull/8269#discussion_r1907549651
   */
  get availableForMultipleOrders(): boolean {
    return this.get('available_for_multiple_orders')
  }

  /**
   * @see https://my-color.esa.io/posts/534
   */
  get parameters(): ParameterList<Parameter> {
    const raw: any[] = this.get('parameters.purchase', [])
    const parameters = _.compact(raw.map(p => (new ParameterFactory(p)).create().fold(
      (error: Error) => {
        log.error({
          message: 'Error on creating payment parameters for purchase from API response.',
          payment: p,
          error,
        })

        return null
      },
      (parameter: Parameter) => parameter
    )))

    return ParameterList.valueOf(parameters)
  }

  /**
   * @see https://my-color.esa.io/posts/534
   */
  get parametersForEstimate(): ParameterList<Parameter> {
    const raw: any[] = this.get('parameters.purchase_estimate', [])
    const parameters = _.compact(raw.map(p => (new ParameterFactory(p)).create().fold(
      (error: Error) => {
        log.error({
          message: 'Error on creating payment parameters for estimate from API response.',
          payment: p,
          error,
        })

        return null
      },
      (parameter: Parameter) => parameter
    )))

    return ParameterList.valueOf(parameters)
  }

  get userSelectParameters(): ParameterList<UserSelect> {
    return this.parameters.onlyUserSelect
  }

  toString(): string {
    return this.id
  }
}

export class PaymentList {

  static valueOf(payments: Payment[]): PaymentList {
    return new PaymentList(payments)
  }

  static empty(): PaymentList {
    return new PaymentList([])
  }

  private constructor(
    private payments: Payment[]
  ) {}

  get list(): Payment[] {
    return this.payments
  }

  get isEmpty(): boolean {
    return this.payments.length === 0
  }

  isAvailablePayment(paymentId: string): boolean {
    return _.some(this.payments, p => p.id === paymentId)
  }

  some(predicate: (payment: Payment) => boolean): boolean {
    return _.some(this.payments, predicate)
  }

  find(predicate: (payment: Payment) => boolean): Payment | null {
    return _.find(this.payments, predicate) || null
  }
}
